import { AuthorProfileCard, type AuthorProfile } from "@/components/blog/AuthorProfileCard";
import { PostCard } from "@/components/blog/PostCard";
import { PostNotFound } from "@/components/blog/PostNotFound";
import { PostPageShell, PostPageSkeleton } from "@/components/blog/PostPageShell";
import { PublicRoutes } from "@/constants/routes";
import { Alert, Button, Heading, Text } from "@/design-system";
import { usePublishedPosts } from "@/hooks/blog";
import { DEFAULT_ORIGIN } from "@/shared/blog/seo";
import { slugify } from "@/shared/blog/slug";
import { Home } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { Link, useParams } from "react-router";

/**
 * Quantos posts publicados varrer para achar os do autor.
 *
 * `ListPublishedParams` não tem filtro por autor (contrato em
 * `blogService.contract.ts`, fora do escopo desta tela), então a página pede
 * uma página grande e separa no cliente. O acervo publicado é pequeno; quando
 * passar de 200 posts isso vira paginação de verdade — e aí já deve existir o
 * endpoint de autor.
 */
const POOL_SIZE = 200;

/**
 * Página pública de autor (`/blog/autor/<slug>`). É E-E-A-T: em conteúdo sobre
 * dinheiro, quem assina importa.
 *
 * ── O QUE FALTA DO BACKEND ──────────────────────────────────────────────────
 * Não existe `BlogAuthor` no domínio: `BlogPost` carrega só `authorName`
 * (string) e `authorId`. Não há endpoint, service nem hook para buscar autor.
 * Então esta página **deriva** o autor dos posts publicados: o slug da URL é
 * comparado com `slugify(authorName)`, e o nome vem do primeiro post que casa.
 *
 * Para a página ficar completa, o backend precisa entregar um `BlogAuthor`
 * com: `slug`, `name`, `role`, `bio`, `photoUrl`, `linkedinUrl` — e o post
 * precisa referenciar esse slug (hoje `authorId` não é resolvível pelo
 * cliente). O `AuthorProfileCard` já recebe tudo isso por prop; falta só quem
 * preencha.
 *
 * Enquanto foto, cargo e bio não existem, a página é **thin content**: sai com
 * `noindex, follow`. Indexar um perfil que só repete o nome e a lista de posts
 * não constrói autoridade nenhuma — e `follow` mantém os links dos posts
 * valendo. Assim que houver bio, a meta vira `index, follow` sozinha.
 */
export const BlogAuthorPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const { data, isLoading, isError, refetch, isFetching } = usePublishedPosts({
    pageSize: POOL_SIZE,
  });

  if (isLoading) return <PostPageSkeleton />;

  if (isError) {
    return (
      <PostPageShell>
        <Alert tone="danger" title="Não foi possível carregar os conteúdos deste autor">
          <div className="flex flex-col items-start gap-3">
            <Text size="small" tone="muted">
              Verifique sua conexão e tente de novo. Os artigos seguem disponíveis na
              listagem do blog.
            </Text>
            <Button
              variant="secondary"
              isLoading={isFetching}
              loadingLabel="Carregando os conteúdos"
              onClick={() => refetch()}
            >
              Tentar de novo
            </Button>
          </div>
        </Alert>
      </PostPageShell>
    );
  }

  const posts = (data?.items ?? []).filter(
    p => p.authorName && slugify(p.authorName) === slug
  );

  // Nenhum post publicado com esse autor: é 404 de verdade. Sem `BlogAuthor`,
  // o autor só existe enquanto assina algum post no ar.
  if (posts.length === 0) return <PostNotFound />;

  const author: AuthorProfile = {
    name: posts[0].authorName as string,
    // role, bio, photoUrl e linkedinUrl ficam vazios até existir `BlogAuthor`.
  };

  const temPerfilCompleto = Boolean(author.bio);
  const canonical = `${DEFAULT_ORIGIN}/blog/autor/${slug}`;

  return (
    <>
      <Helmet>
        <title>{`${author.name} — autor no Blog da Fidexa`}</title>
        <meta
          name="description"
          content={`Artigos sobre crediário, cobrança e inadimplência escritos por ${author.name} no Blog da Fidexa.`}
        />
        <link rel="canonical" href={canonical} />
        <meta
          name="robots"
          content={temPerfilCompleto ? "index, follow" : "noindex, follow"}
        />
      </Helmet>

      <PostPageShell>
        <nav aria-label="Trilha de navegação" className="mb-6">
          <ol className="font-ds-sans text-ds-small text-ds-ink-600 flex flex-wrap items-center gap-1.5">
            <li>
              <Link
                to={PublicRoutes.HOME}
                className="rounded-ds-sm hover:text-ds-brand-700 focus-visible:ring-ds-focus inline-flex cursor-pointer items-center gap-1 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
              >
                <Home size={14} aria-hidden="true" />
                Início
              </Link>
            </li>
            <li aria-hidden="true">/</li>
            <li>
              <Link
                to={PublicRoutes.BLOG}
                className="rounded-ds-sm hover:text-ds-brand-700 focus-visible:ring-ds-focus cursor-pointer focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
              >
                Blog
              </Link>
            </li>
            <li aria-hidden="true">/</li>
            <li className="text-ds-ink-800" aria-current="page">
              {author.name}
            </li>
          </ol>
        </nav>

        <AuthorProfileCard author={author} />

        <section aria-labelledby="posts-do-autor" className="mt-12">
          <Heading level={2} size={3} id="posts-do-autor">
            Artigos de {author.name}
          </Heading>

          <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {posts.map(post => (
              <PostCard key={post.id} post={post} />
            ))}
          </div>
        </section>
      </PostPageShell>
    </>
  );
};
